import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { films } from "@/mocks/films";
import { useSeo } from "@/hooks/useSeo";
import FilmCard from "@/components/feature/FilmCard";
import Breadcrumb from "@/components/feature/Breadcrumb";

const typeOptions = ["全部", "电影", "电视剧"];
const yearOptions = ["全部", "近5年", "5年以上"];

function matchYear(year: number, filter: string) {
  if (filter === "近5年") return year >= 2021;
  if (filter === "5年以上") return year < 2021;
  return true;
}

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get("q") || "";
  const [input, setInput] = useState(query);

  const type = searchParams.get("type") || "全部";
  const year = searchParams.get("year") || "全部";

  useSeo({
    title: query ? `搜索「${query}」 - 顽皮的女朋友影视内容` : "搜索影视 - 顽皮的女朋友",
    description:
      "在顽皮的女朋友影视内容站搜索影视作品，按类型与年份筛选，快速找到想看的影视剧。",
    keywords: "影视搜索,搜索影视,顽皮的女朋友,影视内容",
  });

  const results = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return films.filter((film) => {
      if (type !== "全部" && film.type !== type) return false;
      if (year !== "全部" && !matchYear(film.year, year)) return false;
      if (!keyword) return true;
      const haystack = [
        film.title,
        film.director,
        film.type,
        film.region,
        ...film.genres,
        ...film.cast,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(keyword);
    });
  }, [query, type, year]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "全部") next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  const submit = () => {
    const next = new URLSearchParams();
    const value = input.trim();
    if (value) next.set("q", value);
    if (type !== "全部") next.set("type", type);
    if (year !== "全部") next.set("year", year);
    setSearchParams(next);
  };

  const hotFilms = useMemo(
    () => [...films].sort((a, b) => b.heat - a.heat).slice(0, 4),
    []
  );

  return (
    <div className="px-4 md:px-6 py-8 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />

      <header className="mt-5">
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground-950">
          搜索影视
        </h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600">
          {query ? (
            <>
              关键词：<span className="text-primary-600 font-medium">「{query}」</span>
            </>
          ) : (
            "输入关键词搜索影视名称、主演或类型"
          )}
        </p>
      </header>

      <form
        className="mt-6 flex flex-col sm:flex-row gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="flex-1 flex items-center gap-2 h-12 px-4 rounded-md border border-background-300 focus-within:border-primary-400 bg-background-50">
          <i className="ri-search-line text-lg text-foreground-500"></i>
          <input
            type="text"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="搜索影视名称、主演或类型…"
            className="flex-1 h-full bg-transparent outline-none text-sm text-foreground-950 placeholder:text-foreground-400"
          />
        </div>
        <button
          type="submit"
          className="h-12 px-6 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 font-heading text-sm tracking-wide hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          搜索
        </button>
      </form>

      <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-foreground-500">类型：</span>
          <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/70">
            {typeOptions.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => updateParam("type", option)}
                className={
                  type === option
                    ? "px-3 py-1 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 text-sm cursor-pointer whitespace-nowrap"
                    : "px-3 py-1 rounded-full text-foreground-700 text-sm hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                }
              >
                {option}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-foreground-500">年份：</span>
          <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/70">
            {yearOptions.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => updateParam("year", option)}
                className={
                  year === option
                    ? "px-3 py-1 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 text-sm cursor-pointer whitespace-nowrap"
                    : "px-3 py-1 rounded-full text-foreground-700 text-sm hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                }
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      </div>

      <p className="mt-6 text-sm text-foreground-600">
        共找到 <span className="font-semibold text-foreground-950">{results.length}</span> 条相关影视内容
      </p>

      {results.length > 0 ? (
        <div className="mt-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
          {results.map((film) => (
            <FilmCard key={film.id} film={film} />
          ))}
        </div>
      ) : (
        <div className="mt-5 py-16 flex flex-col items-center text-center rounded-lg bg-background-100 border border-background-200/70">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 mb-4">
            <i className="ri-search-eye-line text-3xl"></i>
          </span>
          <h2 className="font-heading font-semibold text-lg text-foreground-950">
            没有找到相关影视
          </h2>
          <p className="mt-2 text-sm text-foreground-600 max-w-sm">
            换个关键词试试，或从下面的热门影视中挑一部看看。
          </p>
          <div className="mt-7 w-full max-w-3xl">
            <p className="text-sm text-foreground-500 mb-3">热门影视</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {hotFilms.map((film) => (
                <Link
                  key={film.id}
                  to={`/detail/${film.id}`}
                  className="group rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <div className="w-full aspect-[2/3] overflow-hidden bg-secondary-200">
                    <img
                      src={film.poster}
                      alt={`${film.title}海报`}
                      title={`${film.title}影视作品`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <p className="p-2.5 text-sm text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
                    {film.title}
                  </p>
                </Link>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate("/category")}
            className="mt-7 h-11 px-6 inline-flex items-center gap-2 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 font-heading text-sm tracking-wide hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-layout-grid-line"></i>
            浏览全部影视
          </button>
        </div>
      )}
    </div>
  );
}